/**
 * The console's starting theme, when the URL does not name one (doc 05-admin/03 §4).
 *
 * The order is the doc's: the choice this browser last made with the TopBar toggle, else the
 * operating system's `prefers-color-scheme`, else light. A `?theme=` in the URL still wins over
 * all three — that is a shared link opening the way the sender saw it — and is not persisted,
 * so following someone's dark link does not quietly change your own default.
 */
export type Theme = 'light' | 'dark';

/** The one localStorage key the console owns for this. */
export const THEME_STORAGE_KEY = 'bookmypandiit.admin.theme';

function isTheme(value: unknown): value is Theme {
  return value === 'light' || value === 'dark';
}

/* Storage can throw (private windows, blocked site data). A throw means "no stored choice". */
function storedTheme(): Theme | null {
  try {
    const value = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isTheme(value) ? value : null;
  } catch {
    return null;
  }
}

function systemTheme(): Theme {
  if (typeof window.matchMedia !== 'function') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/** The theme to open with when the URL carries none. */
export function initialTheme(): Theme {
  return storedTheme() ?? systemTheme();
}

/** Remember a choice the user made with the toggle. A storage failure is not an error. */
export function persistTheme(theme: Theme): void {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    /* Nothing to do: the choice still holds for this tab through the URL. */
  }
}

/** A URL value, if it is a real theme; anything else falls back to `initialTheme()`. */
export function resolveTheme(fromUrl: unknown): Theme {
  return isTheme(fromUrl) ? fromUrl : initialTheme();
}
